import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { ISaque } from './index.d';
import { useAcoesSaque } from './useAcoesSaque';

const moeda = (valor: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(valor || 0);

const data = (valor?: string) =>
    valor ? format(parseISO(valor), 'dd/MM/yyyy HH:mm') : '';

const COR_STATUS = {
    AGUARDANDO_APROVACAO: '#b26a00',
    PENDENTE: '#0056b3',
    PROCESSANDO: '#0056b3',
    CONCLUIDO: '#1e7e34',
    FALHOU: '#c82333',
    CANCELADO: '#6c757d',
    REPROVADO: '#c82333',
};

export const useTransformSaques = (props) => {
    const saques: ISaque[] = props.response || [];
    const acoes = useAcoesSaque(props);

    return useMemo(() => {
        return saques.map((saque) => {
            const aguardando = saque.status === 'AGUARDANDO_APROVACAO';
            // Falhou ou foi cancelado pelo banco: dá para reenviar ou devolver ao saldo
            const semPagamento =
                saque.status === 'FALHOU' ||
                (saque.status === 'CANCELADO' && !!saque.asaasTransferId);
            const emAndamento =
                saque.status === 'PENDENTE' || saque.status === 'PROCESSANDO';

            const botoes = [
                {
                    titulo: 'Aprovar e pagar',
                    click: acoes.handleAprovar,
                    data: saque,
                    color: 'green',
                    icone: 'icon-certo',
                    hidden: !aguardando,
                },
                {
                    titulo: 'Reenviar ao Asaas',
                    click: acoes.handleReprocessar,
                    data: saque,
                    color: '',
                    icone: 'icon-enviar',
                    hidden: !semPagamento,
                },
                {
                    titulo: 'Reprovar e devolver ao saldo',
                    click: acoes.handleReprovar,
                    data: saque,
                    color: 'red',
                    icone: 'icon-fechar',
                    hidden:
                        !(aguardando || semPagamento) ||
                        !!saque.envioSemConfirmacao,
                },
                {
                    titulo: 'Atualizar status no Asaas',
                    click: acoes.handleSincronizar,
                    data: saque,
                    color: '',
                    icone: 'icon-atualizar',
                    hidden: !saque.asaasTransferId || !(emAndamento || semPagamento),
                },
                {
                    titulo: 'Ver comprovante',
                    click: acoes.handleComprovante,
                    data: saque,
                    color: '',
                    icone: 'icon-invoice',
                    hidden: !saque.comprovanteUrl,
                },
            ].filter((botao) => !botao.hidden);

            const taxa =
                saque.taxaAntecipacao > 0
                    ? saque.taxaTipo === 'fixo'
                        ? 'Taxa fixa'
                        : `${String(saque.taxaValorConfigurado ?? '').replace(
                              '.',
                              ','
                          )}% de antecipação`
                    : 'Sem antecipação';

            return {
                key: saque.id,
                lineClassName: '',
                colunas: [
                    {
                        tipo: 'Texto',
                        width: '18rem',
                        marginRight: '0',
                        titulo: data(saque.dataSolicitacao),
                        subtitulo: `#${saque.id}`,
                    },
                    {
                        tipo: 'Texto',
                        width: '28rem',
                        marginRight: '0',
                        titulo: saque.entregadorNome,
                        subtitulo: `PIX ${saque.tipoPix}: ${saque.chavePix}`,
                    },
                    {
                        tipo: 'Texto',
                        width: '20rem',
                        marginRight: '0',
                        flexDirection: 'row',
                        botoesInline: [
                            {
                                titulo: 'Ver corridas',
                                click: acoes.handleDetalhes,
                                data: saque,
                                color: '',
                                icone: 'icon-visualizar',
                            },
                        ],
                        titulo: moeda(saque.valorSolicitado),
                        subtitulo: `${saque.qtdeColetas} corrida(s)${
                            saque.valorGarantido > 0
                                ? ` + ${moeda(saque.valorGarantido)} garantido`
                                : ''
                        }`,
                    },
                    {
                        tipo: 'Texto',
                        width: '16rem',
                        marginRight: '0',
                        titulo: moeda(saque.taxaAntecipacao),
                        subtitulo: taxa,
                    },
                    {
                        tipo: 'Texto',
                        width: '14rem',
                        marginRight: '0',
                        titulo: moeda(saque.valorLiquido),
                    },
                    {
                        tipo: 'Texto',
                        width: '26rem',
                        marginRight: '0',
                        titulo: saque.statusDescricao,
                        styleTitulo: {
                            color: COR_STATUS[saque.status],
                            fontWeight: 600,
                        },
                        subtitulo:
                            saque.motivoReprovacao || saque.motivoFalha || '',
                    },
                    {
                        tipo: 'Texto',
                        width: '16rem',
                        marginRight: '0',
                        titulo: data(saque.dataPagamento) || '-',
                    },
                    {
                        tipo: 'Texto',
                        width: '18rem',
                        marginRight: '0',
                        titulo: '',
                        botoesInline: botoes,
                    },
                ],
            };
        });
    }, [saques, acoes]);
};
